import React, { Children, cloneElement, FC, isValidElement, ReactElement, ReactNode, useState } from 'react'
import { Button } from 'rsuite'
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine'
import ArrowUpLineIcon from '@rsuite/icons/ArrowUpLine'
import useTranslation from 'next-translate/useTranslation'

interface ReadMoreWrapperProps {
  children: ReactNode
  maxLength?: number
  className?: string
  inlineButton?: boolean
}

/**
 * A wrapper component that adds "Read more"/"Show less" functionality to content.
 * Truncates content if it exceeds the specified maxLength and provides a button to expand/collapse.
 * Works with standalone strings or nested react components.
 *
 * @param children - The string or component to be wrapped
 * @param maxLength - Maximum number of characters to show before truncating (default: 250)
 * @param className - CSS class name to apply to the wrapper div
 *
 * @example
 * <ReadMoreWrapper maxLength={100}>
 *   <p>Some long text content that needs to be truncated...</p>
 * </ReadMoreWrapper>
 */

const ReadMoreWrapper: FC<ReadMoreWrapperProps> = (props) => {
  const { children, maxLength = 250, className = '', inlineButton = false } = props

  const [isExpanded, setIsExpanded] = useState(false)
  const { t } = useTranslation('products')

  // Recursive function to extract text from nested components
  const extractTextFromChildren = (
    childrenNodes: ReactNode,
  ): string => Children.toArray(childrenNodes)
    .map((child) => {
      if (isValidElement(child)) {
        if (child.props.children) {
          return extractTextFromChildren(child.props.children)
        }
        return ''
      }
      return String(child)
    })
    .join(' ')

  const textContent = extractTextFromChildren(children)

  if (!textContent || textContent.length <= maxLength) {
    return <div className={className}>{children}</div>
  }

  // Truncate children while preserving React elements
  const truncateChildren = (
    childrenNodes: ReactNode,
    currentLength = 0,
  ): [ReactNode[], number] => {
    const result: ReactNode[] = []
    let length = currentLength

    Children.forEach(childrenNodes, (child) => {
      // Skip if already at max length
      if (length >= maxLength) return

      if (isValidElement(child) && child.props.children) {
        // Handle nested components
        const [truncatedChildren, newLength] = truncateChildren(child.props.children, length)
        if (truncatedChildren.length > 0) {
          result.push(cloneElement(child as ReactElement, { children: truncatedChildren }))
          length = newLength
        }
      } else {
        // Handle text nodes
        const text = typeof child === 'object' ? '' : String(child)
        const remaining = maxLength - length

        if (remaining <= 0) return

        const truncated = remaining < text.length
          ? `${text.slice(0, remaining)}...`
          : text

        result.push(truncated)
        length += truncated.length
      }
    })

    return [result, length]
  }

  return (
    <div className={isExpanded ? '' : className}>
      <div>
        {isExpanded ? children : truncateChildren(children)[0]}
      </div>
      <Button
        className={`padding-zero ${inlineButton ? '' : 'margin-top-spacer'}`}
        appearance="link"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        {isExpanded ? (
          <>
            {t('Close')}
            <ArrowUpLineIcon className="margin-left-spacer-half" />
          </>
        ) : (
          <>
            {t('marketing:Read more')}
            <ArrowDownLineIcon className="margin-left-spacer-half" />
          </>
        )}
      </Button>
    </div>
  )
}

export default ReadMoreWrapper
